import type { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { ThemeToggle } from '../../app/colorMode';
import { BrandLogo } from '../../components/BrandLogo';
import { brand, tokens } from '../../app/theme';

/** The TailAdmin "grid-01" shape: a faint square grid with a few filled cells, used as decoration. */
function GridShape({ sx }: { sx?: object }) {
  const cells = [
    [2, 1],
    [1, 2],
    [10, 1],
    [9, 2],
  ];
  return (
    <Box component="svg" viewBox="0 0 450 254" aria-hidden sx={{ position: 'absolute', width: { md: 300, lg: 450 }, pointerEvents: 'none', ...sx }}>
      <defs>
        <linearGradient id="grid-fade" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#fff" stopOpacity="0.14" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>
      {Array.from({ length: 10 }, (_, i) => (
        <line key={`v${i}`} x1={i * 51.5 + 0.5} y1="0" x2={i * 51.5 + 0.5} y2="254" stroke="url(#grid-fade)" />
      ))}
      {Array.from({ length: 6 }, (_, i) => (
        <line key={`h${i}`} x1="0" y1={i * 51.5 + 0.5} x2="450" y2={i * 51.5 + 0.5} stroke="url(#grid-fade)" />
      ))}
      {cells.map(([x, y]) => (
        <rect key={`${x}-${y}`} x={(x! - 1) * 51.5 + 1} y={(y! - 1) * 51.5 + 1} width="50.5" height="50.5" fill="#fff" fillOpacity="0.04" />
      ))}
    </Box>
  );
}

export function AuthLayout({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, bgcolor: tokens.surface, position: 'relative' }}>
      {/* Form side */}
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', px: { xs: 3, sm: 6 }, py: 6 }}>
        <Box sx={{ width: '100%', maxWidth: 450, mx: 'auto', animation: 'ta-fade-up .4s cubic-bezier(0.4, 0, 0.2, 1)' }}>
          <Box sx={{ mb: 5, display: { lg: 'none' } }}>
            <BrandLogo height={30} />
            <Typography sx={{ mt: 1, fontSize: 11, fontWeight: 500, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'text.secondary' }}>
              Support Portal
            </Typography>
          </Box>
          <Typography variant="h5" component="h1" sx={{ fontSize: { xs: 24, sm: 30 }, lineHeight: { sm: '38px' }, fontWeight: 600, color: 'text.primary' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography color="text.secondary" sx={{ mt: 1, fontSize: 14 }}>
              {subtitle}
            </Typography>
          )}
          <Box sx={{ mt: 4 }}>{children}</Box>
        </Box>
      </Box>

      {/* Brand side */}
      <Box
        sx={{
          display: { xs: 'none', lg: 'grid' },
          placeItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          bgcolor: brand[950],
          '[data-theme="dark"] &': { bgcolor: 'rgba(255,255,255,0.05)' },
        }}
      >
        <GridShape sx={{ top: 0, right: 0 }} />
        <GridShape sx={{ bottom: 0, left: 0, transform: 'rotate(180deg)' }} />
        <Stack alignItems="center" sx={{ position: 'relative', zIndex: 1, maxWidth: 400, textAlign: 'center' }}>
          <BrandLogo onDark height={52} />
          <Typography sx={{ mt: 2, mb: 3, fontSize: 13, fontWeight: 500, letterSpacing: '0.32em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.75)' }}>
            Support Portal
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 16, lineHeight: 1.6 }}>
            Product &amp; service operations for every store, team and client — tickets, releases and SLAs in one place.
          </Typography>
        </Stack>
      </Box>

      <ThemeToggle floating sx={{ position: 'fixed', right: 24, bottom: 24, zIndex: 10 }} />
    </Box>
  );
}
